import axiosInstance from '../../../config/axios';

export type OfferStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'INVOICED';

// Payment Plan Types
export type PaymentMilestoneType = 'DOWN_PAYMENT' | 'PROGRESS' | 'FINAL';
export type PaymentMilestoneTrigger =
  | 'ON_OFFER_ACCEPTED'
  | 'ON_PROJECT_STATUS'
  | 'ON_DATE'
  | 'MANUAL'
  | 'ON_OPS_PROGRESS';
export type PaymentMilestoneStatus = 'PLANNED' | 'READY_FOR_INVOICE' | 'INVOICED' | 'PAID' | 'CANCELLED';

export interface PaymentMilestone {
  id?: number;
  label: string;
  sequence: number;
  milestone_type: PaymentMilestoneType;
  percentage: number;
  amount_net?: number;
  amount_tax?: number;
  amount_gross?: number;
  trigger_type: PaymentMilestoneTrigger;
  trigger_project_status?: string | null;
  trigger_date?: string | null;
  due_offset_days?: number | null;
  status?: PaymentMilestoneStatus;
  invoice_id?: number | null;
  invoice_number?: string | null;
  invoiced_at?: string | null;
  invoice_sent_at?: string | null;
  tasks_total?: number;
  tasks_done?: number;
  tasks_open?: number;
}

export interface PaymentPlan {
  offer: number;
  currency: string;
  total_net: number;
  total_tax: number;
  total_gross: number;
  scheme_type?: string;
  milestones: PaymentMilestone[];
}

export interface OfferLineItem {
  id?: number;
  position: number;
  catalog_item_id?: number;
  name: string;
  description?: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percent?: number;
  tax_rate?: number;
  total_net: number;
  total_gross: number;
  // FE-BILL-HF-R37-1: Scope items count from backend
  scope_items_count?: number;
  // FE-BILL-HF-R38-4: Payment milestone assignment from offer detail
  payment_milestone_id?: number | null;
  payment_milestone_label?: string | null;
}

export interface Offer {
  id: number;
  offer_number: string;
  project_id: number;
  project_name?: string;
  business_partner_id: number;
  business_partner_name?: string;
  title: string;
  description?: string;
  status: OfferStatus;
  valid_until?: string;
  subtotal_net: number;
  total_tax: number;
  total_gross: number;
  notes?: string;
  terms?: string;
  payment_terms_days?: number | null;
  line_items?: OfferLineItem[];
  execution_notes?: string;
  order_confirmation_number?: string;
  order_confirmed_at?: string;
  sent_at?: string;
  accepted_at?: string;
  invoiced_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateOfferPayload {
  project_id?: number;
  business_partner_id: number;
  title: string;
  description?: string;
  valid_until?: string;
  notes?: string;
  terms?: string;
  payment_terms_days?: number | null;
  line_items?: Omit<OfferLineItem, 'id' | 'total_net' | 'total_gross'>[];
}

export interface UpdateOfferPayload {
  title?: string;
  description?: string;
  status?: OfferStatus;
  valid_until?: string;
  notes?: string;
  terms?: string;
  payment_terms_days?: number | null;
  line_items?: Omit<OfferLineItem, 'id' | 'total_net' | 'total_gross'>[];
}

export interface SendEmailPayload {
  recipient_email: string;
  subject: string;
  message: string;
  send_as_me?: boolean;
}

export interface SendEmailResponse {
  message: string;
  status: OfferStatus;
  sent_to: string;
}

export interface OrderConfirmationEmailPayload {
  to: string;
  subject: string;
  body: string;
}

export interface OrderConfirmationPayload {
  send_email?: boolean;
  email?: OrderConfirmationEmailPayload;
  execution_notes?: string;
}

export interface ConfirmOrderPayload {
  auto_generate_ops_jobs?: boolean;
}

export interface ConfirmOrderOpsJobs {
  created_tasks_count: number;
  created_checklist_items_count: number;
  skipped_existing_count: number;
  processed_milestones_count: number;
}

export interface ConfirmOrderResponse {
  message: string;
  order_confirmed_at: string;
  order_confirmation_number: string;
  ops_jobs?: ConfirmOrderOpsJobs;
}

export interface ConfirmOrderError {
  detail: string;
  code?: 'positions_unassigned' | 'offer_missing_project' | 'milestone_locked';
}

export interface OfferListParams {
  project_id?: number;
  partner_id?: number;
  status?: OfferStatus;
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface BulkStatusChangeResult {
  id: number;
  success: boolean;
  error?: string;
}

export interface BulkStatusChangeResponse {
  status: OfferStatus;
  updated_count: number;
  total_requested: number;
  results: BulkStatusChangeResult[];
}

// FE-OPS-R27-1 / FE-BILL-R33-2: Milestone Assignment Types
export interface MilestoneAssignment {
  position_id: number;
  position_name: string;
  position_amount: number;
  payment_milestone_id: number;
  payment_milestone_label: string;
}

export interface UnassignedPosition {
  id: number;
  name: string;
  amount: number;
}

export interface MilestonePositionInfo {
  id: number;
  name: string;
  amount: number;
}

export interface MilestoneInfo {
  id: number;
  label: string;
  percentage: number;
  status: string;
  amount_net?: number;
  positions_total_net?: number;
  positions?: MilestonePositionInfo[];
}

export interface MilestoneAssignmentsResponse {
  milestones: MilestoneInfo[];
  assignments: MilestoneAssignment[];
  unassigned_positions: UnassignedPosition[];
}

// Backwards-compatible type for components that need a unified position list
export interface OfferLinePosition {
  id: number;
  title: string;
  amount_net: number;
  payment_milestone_id: number | null;
}

export interface AssignOfferLinePayload {
  offer_line_id: number;
  payment_milestone_id: number | null;
}

export interface AssignOfferLineResponse {
  message: 'assignment_saved';
  offer_line_id: number;
  payment_milestone_id: number | null;
}

// FE-BILL-R28-2: OfferLine Scope Items (Snapshot)
export interface OfferLineScopeItem {
  text: string;
  sort_order: number;
}

export interface OfferLineScopeItemsResponse {
  offer_line_id: number;
  scope_items: OfferLineScopeItem[];
}

// FE-BILL-R29-1: Create Invoice Draft from Milestone
export interface CreateInvoiceDraftResult {
  invoice_id: number;
  invoice_number: string;
  status: 'DRAFT';
  offer_id: number;
  project_id: number;
  payment_plan_id: number;
  payment_milestone_id: number;
  payment_milestone_label: string;
  positions_count: number;
  amount_net: number;
  amount_gross: number;
  currency: string;
  message: 'invoice_draft_created' | 'invoice_already_exists';
}

// FE-BILL-R32-1: Offer Templates
export interface OfferTemplateListItem {
  id: number;
  code: string;
  name: string;
  trade: string;
  is_active: boolean;
  milestones_count: number;
  lines_count: number;
}

// FE-BILL-R34-1: Payment Plan Templates
// BE Contract: template_id is the primary key (not id)
export interface PaymentPlanTemplate {
  template_id: number;
  code: string;
  name: string;
  trade?: string;
  milestones_count?: number;
  milestones: PaymentPlanTemplateMilestone[];
}

export interface PaymentPlanTemplateMilestone {
  label: string;
  sequence: number;
  milestone_type: PaymentMilestoneType;
  percentage: number;
  trigger_type: PaymentMilestoneTrigger;
  trigger_project_status?: string | null;
  due_offset_days?: number | null;
}

export interface ApplyPaymentPlanTemplatePayload {
  template_id: number;
  mode: 'REPLACE';
}

export interface ApplyPaymentPlanTemplateResponse {
  message: 'template_applied';
  milestones_created: number;
}

export interface OfferTemplateMilestone {
  label: string;
  sequence: number;
  milestone_type: PaymentMilestoneType;
  percentage: number;
  trigger_type: PaymentMilestoneTrigger;
}

export interface OfferTemplateLine {
  position: number;
  name: string;
  description?: string;
  quantity: number;
  unit: string;
  unit_price: number;
  tax_rate: number;
}

export interface OfferTemplateDetail {
  id: number;
  code: string;
  name: string;
  trade: string;
  description?: string;
  is_active: boolean;
  milestones: OfferTemplateMilestone[];
  lines: OfferTemplateLine[];
}

export interface CreateOfferFromTemplatePayload {
  partner_id: number;
  project_id?: number;
  template_id: number;
  title?: string;
}

export interface CreateOfferFromTemplateResult {
  message: 'offer_created_from_template';
  offer_id: number;
  payment_plan_id: number;
}

export const OFFER_STATUS_OPTIONS: { value: OfferStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT', label: 'Sent' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'EXPIRED', label: 'Expired' },
];

function unwrap<T>(response: { data: T }): T {
  return response.data;
}

interface BackendOfferLine {
  id: number;
  position: number;
  kind: string;
  catalog_item: number | null;
  catalog_item_display: string | null;
  description: string;
  quantity: string;
  unit: string;
  unit_price_net: string;
  discount_percent: string;
  tax_rate: string;
  line_total_net: string;
  line_total_gross: string;
  // FE-BILL-HF-R38-4: Payment milestone assignment from backend
  payment_milestone_id?: number | null;
  payment_milestone_label?: string | null;
  scope_items_count?: number;
}

interface BackendOffer {
  id: number;
  tenant: number;
  partner: number;
  partner_display: string;
  project: number | null;
  project_display: string | null;
  status: OfferStatus;
  status_display: string;
  offer_number: string;
  title: string;
  valid_until: string | null;
  net_total: string;
  tax_total: string;
  gross_total: string;
  currency: string;
  notes_internal: string;
  payment_terms_days: number | null;
  execution_notes?: string;
  order_confirmation_number?: string;
  order_confirmed_at?: string;
  sent_at?: string;
  accepted_at?: string;
  invoiced_at?: string;
  lines: BackendOfferLine[];
  created_at: string;
  updated_at: string;
}

function transformBackendOffer(backend: BackendOffer): Offer {
  return {
    id: backend.id,
    offer_number: backend.offer_number,
    project_id: backend.project || 0,
    project_name: backend.project_display || undefined,
    business_partner_id: backend.partner,
    business_partner_name: backend.partner_display,
    title: backend.title,
    status: backend.status,
    valid_until: backend.valid_until || undefined,
    subtotal_net: parseFloat(backend.net_total) || 0,
    total_tax: parseFloat(backend.tax_total) || 0,
    total_gross: parseFloat(backend.gross_total) || 0,
    notes: backend.notes_internal || undefined,
    payment_terms_days: backend.payment_terms_days,
    line_items: (backend.lines || []).map((line: any) => ({
      // FE-BILL-HF-R35-3: Handle alternative field names for line ID
      id: line.id ?? line.offer_line_id ?? line.line_id ?? line.pk,
      position: line.position,
      catalog_item_id: line.catalog_item || undefined,
      name: line.description.split('\n')[0],
      description: line.description.includes('\n') ? line.description.split('\n').slice(1).join('\n') : undefined,
      quantity: parseFloat(line.quantity) || 0,
      unit: line.unit,
      unit_price: parseFloat(line.unit_price_net) || 0,
      discount_percent: parseFloat(line.discount_percent) || 0,
      tax_rate: parseFloat(line.tax_rate) || 19,
      total_net: parseFloat(line.line_total_net) || 0,
      total_gross: parseFloat(line.line_total_gross) || 0,
      // FE-BILL-HF-R37-1: Scope items count
      scope_items_count: line.scope_items_count ?? 0,
      // FE-BILL-HF-R38-4: Payment milestone assignment
      payment_milestone_id: line.payment_milestone_id ?? line.payment_milestone ?? null,
      payment_milestone_label: line.payment_milestone_label ?? null,
    })),
    execution_notes: backend.execution_notes || undefined,
    order_confirmation_number: backend.order_confirmation_number || undefined,
    order_confirmed_at: backend.order_confirmed_at || undefined,
    sent_at: backend.sent_at || undefined,
    accepted_at: backend.accepted_at || undefined,
    invoiced_at: backend.invoiced_at || undefined,
    created_at: backend.created_at,
    updated_at: backend.updated_at,
  };
}

export default class OfferService {
  static async list(params?: OfferListParams): Promise<Offer[]> {
    const queryParams: Record<string, string> = {};

    if (params?.project_id) {
      queryParams.project_id = String(params.project_id);
    }
    if (params?.partner_id) {
      queryParams.partner_id = String(params.partner_id);
    }
    if (params?.status) {
      queryParams.status = params.status;
    }
    if (params?.search) {
      queryParams.search = params.search;
    }
    if (params?.page) {
      queryParams.page = String(params.page);
    }
    if (params?.page_size) {
      queryParams.page_size = String(params.page_size);
    }
    if (params?.ordering) {
      queryParams.ordering = params.ordering;
    }

    const query = new URLSearchParams(queryParams).toString();
    const url = query ? `/api/admin/offers/?${query}` : '/api/admin/offers/';

    const response = await axiosInstance.get<BackendOffer[] | PaginatedResponse<BackendOffer>>(url);

    if (Array.isArray(response.data)) {
      return response.data.map(transformBackendOffer);
    }
    return response.data.results.map(transformBackendOffer);
  }

  static async listByProject(projectId: number): Promise<Offer[]> {
    return this.list({ project_id: projectId });
  }

  static async listByPartner(partnerId: number): Promise<Offer[]> {
    return this.list({ partner_id: partnerId });
  }

  static async get(id: number): Promise<Offer> {
    const response = await axiosInstance.get<BackendOffer>(`/api/admin/offers/${id}/`);
    return transformBackendOffer(response.data);
  }

  static async create(payload: CreateOfferPayload): Promise<Offer> {
    const backendPayload: Record<string, unknown> = {
      partner: payload.business_partner_id,
      project: payload.project_id || null,
      title: payload.title,
      valid_until: payload.valid_until || null,
      notes_internal: payload.notes || '',
      offer_number: `A-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`,
      lines: (payload.line_items || []).map((item, index) => ({
        position: item.position || index + 1,
        kind: 'ITEM',
        catalog_item: item.catalog_item_id || null,
        description: item.name + (item.description ? `\n${item.description}` : ''),
        quantity: item.quantity,
        unit: item.unit,
        unit_price_net: item.unit_price,
        discount_percent: item.discount_percent || 0,
        tax_rate: item.tax_rate || 19,
      })),
    };
    if (payload.payment_terms_days !== undefined) {
      backendPayload.payment_terms_days = payload.payment_terms_days;
    }
    const response = await axiosInstance.post<BackendOffer>('/api/admin/offers/', backendPayload);
    return transformBackendOffer(response.data);
  }

  static async update(id: number, payload: UpdateOfferPayload): Promise<Offer> {
    const backendPayload: Record<string, unknown> = {};

    if (payload.title !== undefined) backendPayload.title = payload.title;
    if (payload.valid_until !== undefined) backendPayload.valid_until = payload.valid_until;
    if (payload.notes !== undefined) backendPayload.notes_internal = payload.notes;
    if (payload.status !== undefined) backendPayload.status = payload.status;
    if (payload.payment_terms_days !== undefined) backendPayload.payment_terms_days = payload.payment_terms_days;

    if (payload.line_items !== undefined) {
      backendPayload.lines = payload.line_items.map((item, index) => ({
        position: item.position || index + 1,
        kind: 'ITEM',
        catalog_item: item.catalog_item_id || null,
        description: item.name + (item.description ? `\n${item.description}` : ''),
        quantity: item.quantity,
        unit: item.unit,
        unit_price_net: item.unit_price,
        discount_percent: item.discount_percent || 0,
        tax_rate: item.tax_rate || 19,
      }));
    }

    const response = await axiosInstance.patch<BackendOffer>(`/api/admin/offers/${id}/`, backendPayload);
    return transformBackendOffer(response.data);
  }

  static async delete(id: number): Promise<void> {
    await axiosInstance.delete(`/api/admin/offers/${id}/`);
  }

  static async changeStatus(id: number, status: OfferStatus): Promise<Offer> {
    const response = await axiosInstance.patch<BackendOffer>(`/api/admin/offers/${id}/`, { status });
    return transformBackendOffer(response.data);
  }

  static async bulkChangeStatus(
    ids: number[],
    status: OfferStatus
  ): Promise<BulkStatusChangeResponse> {
    const response = await axiosInstance.post<BulkStatusChangeResponse>(
      '/api/admin/offers/bulk-change-status/',
      { ids, status }
    );
    return response.data;
  }

  static async send(id: number): Promise<Offer> {
    return unwrap(await axiosInstance.post<Offer>(`/api/admin/offers/${id}/send/`));
  }

  static async accept(id: number): Promise<Offer & { project_status_updated?: boolean }> {
    return unwrap(await axiosInstance.post<Offer & { project_status_updated?: boolean }>(`/api/admin/offers/${id}/accept/`));
  }

  static async reject(id: number): Promise<Offer> {
    return unwrap(await axiosInstance.post<Offer>(`/api/admin/offers/${id}/reject/`));
  }

  static async downloadPdf(id: number): Promise<Blob> {
    const response = await axiosInstance.get(`/api/admin/offers/${id}/pdf/`, {
      responseType: 'blob',
    });
    return response.data;
  }

  static async renderPdf(id: number): Promise<{ pdf_url: string }> {
    return unwrap(await axiosInstance.post<{ pdf_url: string }>(`/api/admin/offers/${id}/render/`));
  }

  static async getPdfBlob(id: number): Promise<Blob> {
    const response = await axiosInstance.get(`/api/admin/offers/${id}/pdf/`, {
      responseType: 'blob',
    });
    return response.data;
  }

  static async sendEmail(id: number, payload: SendEmailPayload): Promise<SendEmailResponse> {
    const response = await axiosInstance.post<SendEmailResponse>(`/api/admin/offers/${id}/send-email/`, payload);
    return response.data;
  }

  static async createOrderConfirmation(id: number, payload?: OrderConfirmationPayload): Promise<{ message: string; order_confirmed_at: string; order_confirmation_number: string }> {
    const response = await axiosInstance.post<{ message: string; order_confirmed_at: string; order_confirmation_number: string }>(
      `/api/admin/offers/${id}/order-confirmation/`,
      payload || {}
    );
    return response.data;
  }

  /**
   * Confirm order (AB) and auto-generate ops jobs from offer lines.
   * POST /api/admin/offers/{offerId}/confirm-order/
   */
  static async confirmOrder(id: number, payload?: ConfirmOrderPayload): Promise<ConfirmOrderResponse> {
    const response = await axiosInstance.post<ConfirmOrderResponse>(
      `/api/admin/offers/${id}/confirm-order/`,
      payload || { auto_generate_ops_jobs: true }
    );
    return response.data;
  }

  static async revokeOrderConfirmation(id: number): Promise<{ message: string }> {
    const response = await axiosInstance.delete<{ message: string }>(
      `/api/admin/offers/${id}/order-confirmation/`
    );
    return response.data;
  }

  static async getOrderConfirmationPdfBlob(id: number): Promise<Blob> {
    const response = await axiosInstance.get(`/api/admin/offers/${id}/order-confirmation/pdf/`, {
      responseType: 'blob',
    });
    return response.data;
  }

  static async createInvoice(
    id: number,
    kind: 'FULL' | 'DEPOSIT',
    depositPercent?: number
  ): Promise<{ invoice_id: number; invoice_number: string }> {
    const payload: { kind: string; deposit_percent?: number } = { kind };
    if (kind === 'DEPOSIT' && depositPercent !== undefined) {
      payload.deposit_percent = depositPercent;
    }
    const response = await axiosInstance.post<{ invoice_id: number; invoice_number: string }>(
      `/api/admin/offers/${id}/create-invoice/`,
      payload
    );
    return response.data;
  }

  static getStatusLabel(status: OfferStatus): string {
    const option = OFFER_STATUS_OPTIONS.find((opt) => opt.value === status);
    return option?.label || status;
  }

  static getStatusColor(status: OfferStatus): { bg: string; text: string } {
    switch (status) {
      case 'DRAFT':
        return { bg: '#f3f4f6', text: '#374151' };
      case 'SENT':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'ACCEPTED':
        return { bg: '#d1fae5', text: '#065f46' };
      case 'REJECTED':
        return { bg: '#fee2e2', text: '#991b1b' };
      case 'EXPIRED':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'INVOICED':
        return { bg: '#ddd6fe', text: '#5b21b6' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  }

  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }

  // Payment Plan Methods
  static async getPaymentPlan(offerId: number): Promise<PaymentPlan | null> {
    try {
      const res = await axiosInstance.get(`/api/admin/offers/${offerId}/payment-plan/`);
      if (res.status === 204 || !res.data) return null;

      const p = res.data;
      return {
        offer: p.offer,
        currency: p.currency,
        total_net: Number(p.total_net),
        total_tax: Number(p.total_tax),
        total_gross: Number(p.total_gross),
        scheme_type: p.scheme_type,
        milestones: (p.milestones || []).map((m: any) => ({
          id: m.id,
          label: m.label,
          sequence: m.sequence,
          milestone_type: m.milestone_type,
          percentage: m.percentage != null ? Number(m.percentage) : 0,
          amount_net: m.amount_net != null ? Number(m.amount_net) : undefined,
          amount_tax: m.amount_tax != null ? Number(m.amount_tax) : undefined,
          amount_gross: m.amount_gross != null ? Number(m.amount_gross) : undefined,
          trigger_type: m.trigger_type,
          trigger_project_status: m.trigger_project_status,
          trigger_date: m.trigger_date,
          due_offset_days: m.due_offset_days,
          status: m.status,
          invoice_id: m.invoice_id,
          invoice_number: m.invoice_number || null,
          invoiced_at: m.invoiced_at || null,
          tasks_total: m.tasks_total ?? 0,
          tasks_done: m.tasks_done ?? 0,
          tasks_open: m.tasks_open ?? 0,
        })),
      };
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  static async savePaymentPlan(offerId: number, plan: PaymentPlan): Promise<PaymentPlan | null> {
    const payload = {
      scheme_type: plan.scheme_type ?? '',
      milestones: plan.milestones.map((m) => ({
        id: m.id,
        label: m.label,
        sequence: m.sequence,
        milestone_type: m.milestone_type,
        percentage: m.percentage,
        trigger_type: m.trigger_type,
        trigger_project_status: m.trigger_project_status || null,
        trigger_date: m.trigger_date || null,
        due_offset_days: m.due_offset_days ?? null,
      })),
    };
    await axiosInstance.post(`/api/admin/offers/${offerId}/payment-plan/`, payload);
    return OfferService.getPaymentPlan(offerId);
  }

  static async deletePaymentPlan(offerId: number): Promise<void> {
    await axiosInstance.delete(`/api/admin/offers/${offerId}/payment-plan/`);
  }

  static async createInvoiceFromMilestone(milestoneId: number): Promise<{ invoice_id: number; invoice_number: string }> {
    const response = await axiosInstance.post<{ invoice_id: number; invoice_number: string }>(
      `/api/admin/payment-milestones/${milestoneId}/create-invoice/`,
      {}
    );
    return response.data;
  }

  static async changeMilestoneStatus(
    milestoneId: number,
    status: 'PLANNED' | 'READY_FOR_INVOICE' | 'CANCELLED'
  ): Promise<PaymentMilestone> {
    const response = await axiosInstance.post<PaymentMilestone>(
      `/api/admin/payment-milestones/${milestoneId}/change-status/`,
      { status }
    );
    return response.data;
  }

  static getMilestoneTypeLabel(type: PaymentMilestoneType): string {
    switch (type) {
      case 'DOWN_PAYMENT':
        return 'Down Payment';
      case 'PROGRESS':
        return 'Progress';
      case 'FINAL':
        return 'Final';
      default:
        return type;
    }
  }

  static getTriggerTypeLabel(trigger: PaymentMilestoneTrigger): string {
    switch (trigger) {
      case 'ON_OFFER_ACCEPTED':
        return 'On Offer Accepted';
      case 'ON_PROJECT_STATUS':
        return 'On Project Status';
      case 'ON_DATE':
        return 'On Date';
      case 'MANUAL':
        return 'Manual';
      case 'ON_OPS_PROGRESS':
        return 'Nach Jobs erledigt (Plantafel)';
      default:
        return trigger;
    }
  }

  static getMilestoneStatusLabel(status: PaymentMilestoneStatus): string {
    switch (status) {
      case 'PLANNED':
        return 'Planned';
      case 'READY_FOR_INVOICE':
        return 'Ready';
      case 'INVOICED':
        return 'Invoiced';
      case 'PAID':
        return 'Paid';
      default:
        return status;
    }
  }

  static getMilestoneStatusColor(status: PaymentMilestoneStatus): { bg: string; text: string } {
    switch (status) {
      case 'PLANNED':
        return { bg: '#f3f4f6', text: '#374151' };
      case 'READY_FOR_INVOICE':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'INVOICED':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'PAID':
        return { bg: '#d1fae5', text: '#065f46' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  }

  // FE-OPS-R27-1 / FE-BILL-R33-2: Milestone Assignment Methods
  static async getMilestoneAssignments(offerId: number): Promise<MilestoneAssignmentsResponse> {
    const response = await axiosInstance.get<MilestoneAssignmentsResponse>(
      `/api/admin/offers/${offerId}/milestone-assignments/`
    );
    return response.data;
  }

  static async assignOfferLineToMilestone(
    offerId: number,
    offerLineId: number,
    milestoneId: number | null
  ): Promise<AssignOfferLineResponse> {
    const response = await axiosInstance.post<AssignOfferLineResponse>(
      `/api/admin/offers/${offerId}/milestone-assignments/`,
      {
        offer_line_id: offerLineId,
        payment_milestone_id: milestoneId,
      }
    );
    return response.data;
  }

  // ============================================================================
  // FE-BILL-R28-2: OfferLine Scope Items (Snapshot) API
  // CANONICAL URLs - NO tolerant parsing
  // ============================================================================

  static async fetchOfferLineScopeItems(offerLineId: number): Promise<OfferLineScopeItemsResponse> {
    const url = `/api/admin/offers/lines/${offerLineId}/scope-items/`;
    return unwrap(await axiosInstance.get<OfferLineScopeItemsResponse>(url));
  }

  static async updateOfferLineScopeItems(
    offerLineId: number,
    scopeItems: OfferLineScopeItem[]
  ): Promise<OfferLineScopeItemsResponse> {
    const url = `/api/admin/offers/lines/${offerLineId}/scope-items/`;
    return unwrap(await axiosInstance.put<OfferLineScopeItemsResponse>(url, { scope_items: scopeItems }));
  }

  // ============================================================================
  // FE-BILL-R29-1: Create Invoice Draft from Milestone
  // CANONICAL URL: POST /api/admin/offers/{offerId}/milestones/{paymentMilestoneId}/create-invoice-draft/
  // ============================================================================

  static async createInvoiceDraftFromMilestone(
    offerId: number,
    paymentMilestoneId: number,
    options?: { issue_date?: string; due_days?: number; title?: string }
  ): Promise<CreateInvoiceDraftResult> {
    const url = `/api/admin/offers/${offerId}/milestones/${paymentMilestoneId}/create-invoice-draft/`;
    const response = await axiosInstance.post<CreateInvoiceDraftResult>(url, options || {});
    return response.data;
  }

  // ============================================================================
  // FE-BILL-R32-1: Offer Templates
  // CANONICAL URLs
  // ============================================================================

  static async fetchOfferTemplates(): Promise<OfferTemplateListItem[]> {
    const response = await axiosInstance.get<OfferTemplateListItem[] | { results: OfferTemplateListItem[] }>(
      '/api/admin/offers/templates/'
    );
    // Handle both array and paginated response
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data.results || [];
  }

  static async fetchOfferTemplate(templateId: number): Promise<OfferTemplateDetail> {
    const response = await axiosInstance.get<OfferTemplateDetail>(
      `/api/admin/offers/templates/${templateId}/`
    );
    return response.data;
  }

  static async createOfferFromTemplate(
    payload: CreateOfferFromTemplatePayload
  ): Promise<CreateOfferFromTemplateResult> {
    const response = await axiosInstance.post<CreateOfferFromTemplateResult>(
      '/api/admin/offers/from-template/',
      payload
    );
    return response.data;
  }

  // ============================================================================
  // FE-BILL-R34-1: Payment Plan Templates
  // CANONICAL URLs
  // ============================================================================

  static async fetchPaymentPlanTemplates(): Promise<PaymentPlanTemplate[]> {
    const response = await axiosInstance.get<PaymentPlanTemplate[] | { results: PaymentPlanTemplate[] }>(
      '/api/admin/offers/payment-plan-templates/'
    );
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data.results || [];
  }

  static async fetchPaymentPlanTemplate(templateId: number): Promise<PaymentPlanTemplate> {
    const response = await axiosInstance.get<PaymentPlanTemplate>(
      `/api/admin/offers/payment-plan-templates/${templateId}/`
    );
    return response.data;
  }

  static async applyPaymentPlanTemplate(
    offerId: number,
    templateId: number
  ): Promise<ApplyPaymentPlanTemplateResponse> {
    const response = await axiosInstance.post<ApplyPaymentPlanTemplateResponse>(
      `/api/admin/offers/${offerId}/payment-plan/apply-template/`,
      { template_id: templateId, mode: 'REPLACE' }
    );
    return response.data;
  }
}
